import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_CSTM_USR_SRCH,
    SG_CSTM_USR_LIST,
    SG_CSTM_USR_EXCEL_LIST,
} from "./services/PtliUsrRoleService";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

/**
 * Internal role List
 * !==  내부 사용자 역할 목록 ==!
 *
 */
export const PTLI1030001Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const [load, setLoad] = useState(false);

    useResource({
        defaultSchema: [{ area: "roleCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        cstmUsrSrch: useForm({
            defaultSchema: SF_CSTM_USR_SRCH,
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        gridCstmUsrList: useGrid({
            defaultSchema: SG_CSTM_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        gridCstmUsrExcelList: useGrid({
            defaultSchema: SG_CSTM_USR_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getCstmUsrList: useFetch({
            api: (page = grid.gridCstmUsrList.page, size = grid.gridCstmUsrList.size) =>
                APIS.getCstmUsrList(form.cstmUsrSrch.getValues(), page, size),
            enabled: form.cstmUsrSrch.isSubmitted,
            key: [grid.gridCstmUsrList.page, grid.gridCstmUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    ...pgeStore,
                    form: form.cstmUsrSrch.getValues(),
                    page: grid.gridCstmUsrList.page,
                    size: grid.gridCstmUsrList.size,
                });
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Get a excel list of Customs Officer. !== 세관직원 엑셀 목록을 조회한다. ==!
        getCstmUsrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getCstmUsrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.cstmUsrSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.gridCstmUsrExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_Internal User Role_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };
    const render = {
        cstmUsrList: {
            cell: {
                usrNm: (props: any) => {
                    const { value, rowValues } = props;
                    return <Link to={`${URLS.ptli1030002s}/${rowValues.usrDmnId}`}>{value}</Link>;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchClick: form.cstmUsrSrch.handleSubmit(() => fetch.getCstmUsrList.fetch()),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        pgeStore?.form && form.cstmUsrSrch.setValues(pgeStore.form);
        pgeStore?.form && handler.onSrchClick();
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getCstmUsrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.cstmUsrSrch.schema.cstmCd} />
                            <Group.Control {...form.cstmUsrSrch.schema.usrDmnId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmUsrSrch.schema.cstmOfcrNm} />
                            <Group.Control {...form.cstmUsrSrch.schema.cstmOfcrNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmUsrSrch.schema.roleId} />
                            <Group.Control {...form.cstmUsrSrch.schema.usrStatCd} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={form.cstmUsrSrch.reset} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.onSrchClick} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    { <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout> }
                    <Group.Title title={"T_CSTM_OFCR_LIST"} />
                    <Grid
                        {...grid.gridCstmUsrList.grid}
                        data={fetch.getCstmUsrList.data?.cstmUsrList}
                        render={render.cstmUsrList}
                    />
                </Group.Body>
            </Group>
            <Group hidden>
                <Grid {...grid.gridCstmUsrExcelList.grid} data={fetch.getCstmUsrExcelList.data?.cstmUsrExcelList} />
            </Group>
        </Page>
    );
};
